import { useMemo } from 'react';
import { formatUnits } from 'viem';

import { getBalanceRefreshInterval, solana } from '@/constants/chain';

import { useSplTokenBalance } from '@/hooks/useSplTokenBalance';
import { useTokenBalance } from '@/hooks/useTokenBalance';

import { type ChainAddress, isSolanaAddress } from '@/utils/models';

import { fontStyles } from '../../../styles/typography';
import { floorDecimals } from '../../../utils/numbers';
import { commify } from '../../../utils/string';

export type TokenBalanceValue = { value: bigint; decimals: number; uiAmountString?: string };

type UseTokenBalanceValueArgs = {
  accountAddress: ChainAddress | undefined;
  chainId: number | undefined;
  tokenAddress: ChainAddress | undefined;
};

function useTokenBalanceData({ accountAddress, chainId, tokenAddress }: UseTokenBalanceValueArgs) {
  const { data: evmBalance } = useTokenBalance({ address: accountAddress, tokenAddress, chainId });
  const { data: splTokenBalance } = useSplTokenBalance({
    tokenAddress: tokenAddress && isSolanaAddress(tokenAddress) ? tokenAddress : undefined,
    accountAddress: accountAddress && isSolanaAddress(accountAddress) ? accountAddress : undefined,
    refetchInterval: getBalanceRefreshInterval(chainId),
  });

  const balance = useMemo<TokenBalanceValue | undefined>(() => {
    if (evmBalance) return evmBalance;
    if (splTokenBalance) {
      return {
        value: BigInt(splTokenBalance.amount),
        decimals: splTokenBalance.decimals,
        uiAmountString: splTokenBalance.uiAmountString ?? undefined,
      };
    }
    return undefined;
  }, [evmBalance, splTokenBalance]);

  return { balance, splTokenBalance };
}

/** Reads the connected account's native, ERC-20, or SPL token balance. */
export function useTokenBalanceValue(
  args: UseTokenBalanceValueArgs,
): TokenBalanceValue | undefined {
  return useTokenBalanceData(args).balance;
}

export function TokenBalanceText({
  balance,
  chainId,
}: {
  balance: TokenBalanceValue | undefined;
  chainId: number | undefined;
}) {
  const balanceFloat = balance
    ? parseFloat(formatUnits(balance.value, balance.decimals))
    : undefined;
  const displayBalance =
    balanceFloat !== undefined
      ? floorDecimals(balanceFloat, balanceFloat > 100 ? 2 : 6)
      : undefined;

  let balanceUi = '-';
  if (chainId === solana.id && balance?.uiAmountString) {
    balanceUi = balance.uiAmountString;
  } else if (balanceFloat !== undefined && displayBalance !== undefined) {
    balanceUi = `${commify(displayBalance)}${
      displayBalance.toString().length < balanceFloat.toString().length ? '...' : ''
    }`;
  }

  return (
    <p className={fontStyles({ variant: 'BODY_SM', class: 'text-token-gray-700 self-center' })}>
      Balance:&nbsp;
      {balanceUi}
    </p>
  );
}
